import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Eye, Image } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const AdminAboutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    story_title: "",
    story_text1: "",
    story_text2: "",
    food_section_title: "",
    food_section_text: "",
    food_image_url: "",
    values_title: "",
    value1_title: "",
    value1_text: "",
    value2_title: "",
    value2_text: "",
    value3_title: "",
    value3_text: "",
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/about-content`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching content:", error);
      toast.error("Kunne ikke laste innhold");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/about-content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Om Oss innhold lagret!");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Kunne ikke lagre innhold");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#fdf8f0' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#721111' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#fdf8f0' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 shadow-md" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/panel050623")}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-white">Rediger Om Oss</h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => window.open("/om-oss", "_blank")}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <Eye className="h-4 w-4 mr-2" />
              Forhåndsvis
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{ background: '#d4af37', color: '#2d1810' }}
              className="font-bold"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Lagrer..." : "Lagre"}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        
        {/* Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-lg"
          style={{ border: '2px solid #b27f47' }}
        >
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#721111' }}>
            Vår Historie
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#2d1810' }}>Tittel</Label>
              <Input
                value={content.story_title}
                onChange={(e) => handleChange("story_title", e.target.value)}
                placeholder="F.eks: Autentisk Indisk Smak i Harstad"
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
            
            <div>
              <Label style={{ color: '#2d1810' }}>Første Avsnitt</Label>
              <Textarea
                value={content.story_text1}
                onChange={(e) => handleChange("story_text1", e.target.value)}
                placeholder="Skriv om restaurantens historie..."
                rows={4}
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
            
            <div>
              <Label style={{ color: '#2d1810' }}>Andre Avsnitt</Label>
              <Textarea
                value={content.story_text2}
                onChange={(e) => handleChange("story_text2", e.target.value)}
                placeholder="Fortsett historien..."
                rows={4}
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
          </div>
        </motion.div>

        {/* Food Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-lg"
          style={{ border: '2px solid #b27f47' }}
        >
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#721111' }}>
            Vår Mat Seksjon
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#2d1810' }}>Seksjon Tittel</Label>
              <Input
                value={content.food_section_title}
                onChange={(e) => handleChange("food_section_title", e.target.value)}
                placeholder="F.eks: Tradisjonelle Oppskrifter"
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
            
            <div>
              <Label style={{ color: '#2d1810' }}>Beskrivelse</Label>
              <Textarea
                value={content.food_section_text}
                onChange={(e) => handleChange("food_section_text", e.target.value)}
                placeholder="Skriv om maten..."
                rows={4}
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
            
            <div>
              <Label style={{ color: '#2d1810' }} className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Bilde URL
              </Label>
              <Input
                value={content.food_image_url}
                onChange={(e) => handleChange("food_image_url", e.target.value)}
                placeholder="https://..."
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
              {content.food_image_url && (
                <div className="mt-2 rounded-lg overflow-hidden" style={{ maxWidth: '300px' }}>
                  <img src={content.food_image_url} alt="Forhåndsvisning" className="w-full h-auto" />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-lg"
          style={{ border: '2px solid #b27f47' }}
        >
          <h2 className="text-lg font-bold mb-4" style={{ color: '#721111' }}>
            Våre Verdier
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#2d1810' }}>Seksjon Tittel</Label>
              <Input
                value={content.values_title}
                onChange={(e) => handleChange("values_title", e.target.value)}
                placeholder="F.eks: Våre Verdier"
                className="mt-1"
                style={{ borderColor: '#b27f47' }}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {/* Value 1 */}
              <div className="p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                <Label style={{ color: '#721111' }}>Verdi 1 - Tittel</Label>
                <Input
                  value={content.value1_title}
                  onChange={(e) => handleChange("value1_title", e.target.value)}
                  className="mt-1 mb-2"
                  style={{ borderColor: '#b27f47' }}
                />
                <Label style={{ color: '#721111' }}>Tekst</Label>
                <Textarea
                  value={content.value1_text}
                  onChange={(e) => handleChange("value1_text", e.target.value)}
                  rows={2}
                  className="mt-1"
                  style={{ borderColor: '#b27f47' }}
                />
              </div>
              
              {/* Value 2 */}
              <div className="p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                <Label style={{ color: '#721111' }}>Verdi 2 - Tittel</Label>
                <Input
                  value={content.value2_title}
                  onChange={(e) => handleChange("value2_title", e.target.value)}
                  className="mt-1 mb-2"
                  style={{ borderColor: '#b27f47' }}
                />
                <Label style={{ color: '#721111' }}>Tekst</Label>
                <Textarea
                  value={content.value2_text}
                  onChange={(e) => handleChange("value2_text", e.target.value)}
                  rows={2}
                  className="mt-1"
                  style={{ borderColor: '#b27f47' }}
                />
              </div>
              
              {/* Value 3 */}
              <div className="p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                <Label style={{ color: '#721111' }}>Verdi 3 - Tittel</Label>
                <Input
                  value={content.value3_title}
                  onChange={(e) => handleChange("value3_title", e.target.value)}
                  className="mt-1 mb-2"
                  style={{ borderColor: '#b27f47' }}
                />
                <Label style={{ color: '#721111' }}>Tekst</Label>
                <Textarea
                  value={content.value3_text}
                  onChange={(e) => handleChange("value3_text", e.target.value)}
                  rows={2}
                  className="mt-1"
                  style={{ borderColor: '#b27f47' }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Save Button at Bottom */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            style={{ background: '#721111', color: 'white' }}
            className="px-8"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Lagrer..." : "Lagre Endringer"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAboutPage;
